/**
 * availabilityFormatter — i18n engine for scheduled product availability messages.
 *
 * The backend returns structured data (availability_key + availability_params).
 * This module converts that into human-readable text for the current restaurant locale.
 *
 * Keys emitted by the backend:
 *   "available_today"      params: { start, end }
 *   "available_tomorrow"   params: { start, end }
 *   "available_on_weekday" params: { weekday, start, end }
 *   "unavailable"          params: {}
 *
 * Locale resolution: restaurant.locale ("es-ES", "pt-PT", ...) → language prefix → dict.
 * Fallback chain: resolved lang → "es" → hardcoded string.
 */

const TRANSLATIONS = {
  pt: {
    days: {
      monday: "segunda-feira", tuesday: "terça-feira", wednesday: "quarta-feira",
      thursday: "quinta-feira", friday: "sexta-feira", saturday: "sábado", sunday: "domingo",
    },
    days_short: {
      monday: "seg", tuesday: "ter", wednesday: "qua",
      thursday: "qui", friday: "sex", saturday: "sáb", sunday: "dom",
    },
    days_article: {
      monday: "na", tuesday: "na", wednesday: "na",
      thursday: "na", friday: "na", saturday: "no", sunday: "no",
    },
    available_today:      "Disponível hoje das {start} às {end}",
    available_tomorrow:   "Disponível amanhã das {start} às {end}",
    available_on_weekday: "Disponível {article} {weekday} das {start} às {end}",
    unavailable:          "Produto indisponível no momento",
    badge_today:          "Hoje",
    badge_tomorrow:       "Amanhã",
    badge_weekday:        "Só {weekday_short}",
    badge_unavailable:    "Indisponível",
  },

  es: {
    days: {
      monday: "lunes", tuesday: "martes", wednesday: "miércoles",
      thursday: "jueves", friday: "viernes", saturday: "sábado", sunday: "domingo",
    },
    days_short: {
      monday: "lun", tuesday: "mar", wednesday: "mié",
      thursday: "jue", friday: "vie", saturday: "sáb", sunday: "dom",
    },
    days_article: {
      monday: "el", tuesday: "el", wednesday: "el",
      thursday: "el", friday: "el", saturday: "el", sunday: "el",
    },
    available_today:      "Disponible hoy de {start} a {end}",
    available_tomorrow:   "Disponible mañana de {start} a {end}",
    available_on_weekday: "Disponible {article} {weekday} de {start} a {end}",
    unavailable:          "No disponible en este momento",
    badge_today:          "Hoy",
    badge_tomorrow:       "Mañana",
    badge_weekday:        "Solo {weekday_short}",
    badge_unavailable:    "No disponible",
  },

  en: {
    days: {
      monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday",
      thursday: "Thursday", friday: "Friday", saturday: "Saturday", sunday: "Sunday",
    },
    days_short: {
      monday: "Mon", tuesday: "Tue", wednesday: "Wed",
      thursday: "Thu", friday: "Fri", saturday: "Sat", sunday: "Sun",
    },
    days_article: {
      monday: "on", tuesday: "on", wednesday: "on",
      thursday: "on", friday: "on", saturday: "on", sunday: "on",
    },
    available_today:      "Available today from {start} to {end}",
    available_tomorrow:   "Available tomorrow from {start} to {end}",
    available_on_weekday: "Available {article} {weekday} from {start} to {end}",
    unavailable:          "Unavailable at the moment",
    badge_today:          "Today",
    badge_tomorrow:       "Tomorrow",
    badge_weekday:        "Only {weekday_short}",
    badge_unavailable:    "Unavailable",
  },

  fr: {
    days: {
      monday: "lundi", tuesday: "mardi", wednesday: "mercredi",
      thursday: "jeudi", friday: "vendredi", saturday: "samedi", sunday: "dimanche",
    },
    days_short: {
      monday: "lun", tuesday: "mar", wednesday: "mer",
      thursday: "jeu", friday: "ven", saturday: "sam", sunday: "dim",
    },
    days_article: {
      monday: "le", tuesday: "le", wednesday: "le",
      thursday: "le", friday: "le", saturday: "le", sunday: "le",
    },
    available_today:      "Disponible aujourd'hui de {start} à {end}",
    available_tomorrow:   "Disponible demain de {start} à {end}",
    available_on_weekday: "Disponible {article} {weekday} de {start} à {end}",
    unavailable:          "Non disponible pour le moment",
    badge_today:          "Aujourd'hui",
    badge_tomorrow:       "Demain",
    badge_weekday:        "Seulement {weekday_short}",
    badge_unavailable:    "Indisponible",
  },

  it: {
    days: {
      monday: "lunedì", tuesday: "martedì", wednesday: "mercoledì",
      thursday: "giovedì", friday: "venerdì", saturday: "sabato", sunday: "domenica",
    },
    days_short: {
      monday: "lun", tuesday: "mar", wednesday: "mer",
      thursday: "gio", friday: "ven", saturday: "sab", sunday: "dom",
    },
    days_article: {
      monday: "il", tuesday: "il", wednesday: "il",
      thursday: "il", friday: "il", saturday: "il", sunday: "la",
    },
    available_today:      "Disponibile oggi dalle {start} alle {end}",
    available_tomorrow:   "Disponibile domani dalle {start} alle {end}",
    available_on_weekday: "Disponibile {article} {weekday} dalle {start} alle {end}",
    unavailable:          "Non disponibile al momento",
    badge_today:          "Oggi",
    badge_tomorrow:       "Domani",
    badge_weekday:        "Solo {weekday_short}",
    badge_unavailable:    "Non disponibile",
  },

  ar: {
    days: {
      monday: "الإثنين", tuesday: "الثلاثاء", wednesday: "الأربعاء",
      thursday: "الخميس", friday: "الجمعة", saturday: "السبت", sunday: "الأحد",
    },
    days_short: {
      monday: "إث", tuesday: "ثل", wednesday: "أر",
      thursday: "خم", friday: "جم", saturday: "سب", sunday: "أح",
    },
    days_article: {
      monday: "يوم", tuesday: "يوم", wednesday: "يوم",
      thursday: "يوم", friday: "يوم", saturday: "يوم", sunday: "يوم",
    },
    available_today:      "متاح اليوم من {start} إلى {end}",
    available_tomorrow:   "متاح غداً من {start} إلى {end}",
    available_on_weekday: "متاح {article} {weekday} من {start} إلى {end}",
    unavailable:          "غير متاح حالياً",
    badge_today:          "اليوم",
    badge_tomorrow:       "غداً",
    badge_weekday:        "فقط {weekday_short}",
    badge_unavailable:    "غير متاح",
  },
};

/** Extract language code from locale string (e.g. "es-ES" → "es"). */
export function resolveLocale(locale) {
  if (!locale) return "es";
  const lang = locale.split("-")[0].toLowerCase();
  return TRANSLATIONS[lang] ? lang : "es";
}

function _fill(template, vars) {
  return Object.entries(vars).reduce(
    (t, [k, v]) => t.replace(new RegExp(`\\{${k}\\}`, "g"), v ?? ""),
    template
  );
}

/**
 * Full human-readable message shown below the product name when blocked.
 * Returns empty string if the product is available (key is empty/null).
 * If `customLabel` is provided (admin-set message), it takes priority.
 */
export function formatAvailabilityMessage(key, params, locale, customLabel) {
  if (customLabel) return customLabel;
  if (!key) return "";

  const lang = resolveLocale(locale);
  const dict = TRANSLATIONS[lang];
  if (!dict || !dict[key]) return dict?.unavailable ?? "Indisponível";

  const weekday = params?.weekday;
  return _fill(dict[key], {
    start:   params?.start   ?? "",
    end:     params?.end     ?? "",
    weekday: weekday ? (dict.days[weekday] ?? weekday) : "",
    article: weekday ? (dict.days_article[weekday] ?? "") : "",
  });
}

/**
 * Short badge text shown on the blocked card (e.g. "Solo mié", "Amanhã").
 * Returns empty string when the product is available.
 */
export function formatAvailabilityBadge(key, params, locale) {
  if (!key) return "";

  const lang = resolveLocale(locale);
  const dict = TRANSLATIONS[lang];
  if (!dict) return "";

  const badgeKey =
    key === "available_today"      ? "badge_today"
    : key === "available_tomorrow"   ? "badge_tomorrow"
    : key === "available_on_weekday" ? "badge_weekday"
    : "badge_unavailable";

  const weekday = params?.weekday;
  return _fill(dict[badgeKey] ?? dict.badge_unavailable ?? "", {
    weekday_short: weekday ? (dict.days_short[weekday] ?? weekday) : "",
    start:         params?.start ?? "",
    end:           params?.end   ?? "",
  });
}

/**
 * Build a compact summary of schedule slots for admin preview.
 * e.g. [{ days:["monday","tuesday"], start_time:"11:00", end_time:"15:00" }]
 * → "seg, ter: 11:00–15:00"
 */
export function formatSlotsSummary(slots, locale) {
  if (!slots || slots.length === 0) return "";
  const lang = resolveLocale(locale);
  const dict = TRANSLATIONS[lang] ?? TRANSLATIONS.es;

  return slots
    .map(s => {
      const dayLabels = (s.days || [])
        .map(d => dict.days_short[d.toLowerCase()] ?? d)
        .join(", ");
      return `${dayLabels}: ${s.start_time ?? ""}–${s.end_time ?? ""}`;
    })
    .join(" | ");
}
